import { fmtCompact } from "../format"

export function friendlyJoinError(raw: string): string | null {
  const code = raw.trim().toLowerCase()
  if (code === "" || code === "join_failed") return "Could not join the bakery."
  if (code === "forbidden" || code === "4403") return "Access denied — reinstall the app or pick another bakery."
  if (code === "unauthorized" || code === "4401" || code === "missing_token") return "Session expired — sign in again."
  if (code === "bad_guild_id") return "Invalid bakery link — pick another server."
  if (code === "unknown_guild" || code === "unknown_bakery" || code === "bakery_not_found" || code === "404" || code.includes("bakery_not_found") || code.includes("unknown_bakery") || code.includes("unknown_guild")) return "Creating bakery..."
  if (code === "bad_click_id" || code === "bad_json" || code === "unknown_type") return "Connection interrupted — retrying."
  if (code === "ws-timeout" || code === "ws-error" || code === "ws-closed") return "Could not reach the bakery. Retrying…"
  if (code.includes("404")) return "Creating bakery..."
  if (code === "not_in_bakery" || code.includes("not_in_bakery")) return "Choose a bakery first"
  return null
}

export function isJoinRelatedError(code: string): boolean {
  const c = code.trim().toLowerCase()
  return c === "" || c === "join_failed" || c === "forbidden" || c === "4403" || c === "unauthorized" || c === "4401" || c === "missing_token" || c === "bad_guild_id" || c.includes("unknown_guild") || c.includes("unknown_bakery") || c.includes("bakery_not_found") || c === "ws-timeout" || c === "ws-error" || c === "ws-closed" || c === "not_in_bakery" || c.includes("not_in_bakery")
}

export function bakerCreditsFor(amount: number): number {
  if (amount <= 0) return 0
  const v = Math.ceil(amount / 2)
  return v > 0 ? v : 1
}

export function friendlyBuyError(raw: string): string {
  const c = raw.trim().toLowerCase()
  if (c === "insufficient_credits") return "Not enough Baker Credits."
  if (c === "insufficient_cookies") return "Not enough bakery cookies."
  if (c.startsWith("insufficient_credits")) return raw.trim()
  if (c.startsWith("not enough")) return raw.trim()
  if (c === "maxed") return "Already maxed."
  if (c === "already_owned") return "Already owned."
  if (c === "already_equipped") return "Already equipped."
  if (c === "not_owned") return "You don't own that yet."
  if (c === "invalid_cosmetic") return "Unknown cosmetic."
  if (c === "invalid_slot") return "Unknown slot."
  if (c === "locked") return "Locked — level up first."
  if (c === "locked_spec") return "Locked — needs required spec present."
  if (c === "locked_tier") return "Locked — upgrade the requirement first."
  if (c === "invalid_upgrade" || c === "invalid_module" || c === "invalid_project" || c === "invalid_spec") return "Unknown item."
  if (c === "completed") return "Project already completed."
  if (c === "in_progress") return "Project already under construction."
  if (c === "busy") return "Construction crew busy — one project at a time."
  if (c === "on_cooldown") return "Ability recharging."
  if (c === "unknown_ability") return "Unknown ability."
  if (c === "no_golden_cookie") return "No golden cookie right now."
  if (c === "already_chosen") return "Already chosen."
  if (c === "already_claimed") return "Quest already claimed."
  if (c === "not_completed") return "Quest not completed yet."
  if (c === "claim_failed") return "Claim failed — try again."
  if (c === "invalid_quest") return "Unknown quest."
  if (c === "exclusive") return "Locked — rival branch already built."
  if (c === "cooldown") return "Construction crew busy — wait for cooldown."
  if (c === "not_in_bakery") return "Choose a bakery first."
  if (c !== "") return c
  return "Buy failed."
}

export type BuyFailureInfo = { reason: string; need?: string; have?: string; level?: number; cooldownRemainingMs?: number }

export function buyFailureText(info: BuyFailureInfo, fallbackNeed?: number, fallbackHave?: number, currency?: string): string {
  const reason = (info.reason ?? "").trim()
  const gate = friendlyBuyError(reason)
  const rc = reason.trim().toLowerCase()
  if (rc === "on_cooldown") {
    const ms = typeof info.cooldownRemainingMs === "number" && Number.isFinite(info.cooldownRemainingMs) ? Math.max(0, info.cooldownRemainingMs) : 0
    return ms > 0 ? `Ability recharging (${Math.ceil(ms / 1000)}s left).` : "Ability recharging."
  }
  const needStr = info.need ?? (fallbackNeed !== undefined && fallbackNeed > 0 ? String(Math.floor(fallbackNeed)) : undefined)
  const haveStr = info.have ?? (fallbackHave !== undefined ? String(Math.floor(Math.max(0, fallbackHave))) : undefined)
  if ((rc === "insufficient_cookies" || rc === "insufficient_credits") && needStr !== undefined && haveStr !== undefined) {
    const label = rc === "insufficient_cookies" ? "Not enough bakery cookies" : "Not enough Baker Credits"
    const coin = currency ?? (rc === "insufficient_cookies" ? "🍪" : "🪙")
    return `${label} (need ${coin} ${fmtCompact(needStr)}, have ${fmtCompact(haveStr)}).`
  }
  if (typeof info.level === "number" && info.level > 0 && (rc === "maxed" || rc.startsWith("locked"))) {
    return `${gate} (Lv ${info.level})`
  }
  return gate
}

export function buySuccessText(kind: string, name: string, level: number | undefined, cost: string | undefined, personalCost?: string): string {
  const coin = kind === "upgrade" ? "🪙" : "🍪"
  if (kind === "cosmetic") {
    return cost !== undefined ? `✅ ${name} owned (${coin} ${fmtCompact(cost)}).` : `✅ ${name} owned.`
  }
  if (kind === "equip") {
    return name !== "" ? `✅ ${name} equipped.` : "✅ Unequipped."
  }
  if (kind === "ability") {
    return cost !== undefined ? `✨ ${name} activated (🪙 ${fmtCompact(cost)}).` : `✨ ${name} activated.`
  }
  if (kind === "project") {
    if (cost !== undefined && personalCost !== undefined) return `✅ ${name} started (🍪 ${fmtCompact(cost)} + 🪙 ${fmtCompact(personalCost)} personal).`
    return cost !== undefined ? `✅ ${name} started (${coin} ${fmtCompact(cost)}).` : `✅ ${name} started.`
  }
  if (typeof level === "number" && level > 0) {
    return cost !== undefined ? `✅ ${name} → Lv ${level} (${coin} ${fmtCompact(cost)}).` : `✅ ${name} → Lv ${level}.`
  }
  return `✅ ${name} confirmed.`
}
